import PageHeading from "../components/PageHeading";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../axios/AxiosInstance";

import { toast } from "sonner";
import { formatDateYearString } from "../utils/formatageDate";
import { Button } from "../components/reusableComponents/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/reusableComponents/Popover";
import { Label } from "../components/reusableComponents/Label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "../components/reusableComponents/Dialog";
import { Badge } from "../components/reusableComponents/Badge";
import { Tabs, TabsList, TabsTrigger } from "../ui/Tabs";
import {
  RadioGroup,
  RadioGroupItem,
} from "../components/reusableComponents/RadioGroup";
import { ClientRelanceModal } from "./clients/ClientRelanceModal";
import { FactureModal } from "./facture/FactureModal";
import { PartialPaymentModal } from "./facture/PartialPaymentModal";
import { FreelanceFactureModal } from "./facture/FreelanceFactureModal";
import { Loader2 } from "lucide-react";

export default function ClientBillTest() {

  // États pour la section principale
  const [activeSection, setActiveSection] = useState("clients"); // "clients" ou "freelance"
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [currentTab, setCurrentTab] = useState("all");
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");
  const [openModal, setOpenModal] = useState(false);
  const [openPartialPaymentModal, setOpenPartialPaymentModal] = useState(false);
  const [selectedFacture] = useState(null);

  // États pour les factures clients
  const [bills, setBills] = useState([]);

  // États pour les factures freelance vers agence
  const [freelanceBills, setFreelanceBills] = useState([]);
  const [openFreelanceModal, setOpenFreelanceModal] = useState(false);

  const [, setPopoverOpen] = useState(false);
  const [, setSelectedBill] = useState(null);
  const [isRelanceOpen, setIsRelanceOpen] = useState(false);
  const [openRelance, setOpenRelance] = useState(false);
  const [niveauRelance, setNiveauRelance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // 3. Fonction appelée quand l’utilisateur clique sur "Enregistrer" dans la modal

  useEffect(() => {
    loadBills();
    loadFreelanceBills();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const formatMontant = (montant) =>
    new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(montant);

  const handleDownloadPdf = async (facture) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`facture/${facture._id}`);

      // On suppose que response.data.data contient le PDF en base64
      const base64Data = response.data.data;

      // Décoder le base64 en données binaires
      const binaryData = Uint8Array.from(atob(base64Data), (char) =>
        char.charCodeAt(0),
      );

      // Créer un Blob à partir des données binaires
      const pdfBlob = new Blob([binaryData], { type: "application/pdf" });

      // Créer une URL temporaire pour le Blob
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Ouvrir dans un nouvel onglet
      window.open(pdfUrl, "_blank");

      // Libérer l’URL après 10 secondes
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);

      toast.success("PDF ouvert avec succès");
    } catch (error) {
      console.error("Erreur lors du téléchargement du PDF:", error);
      toast.error("Une erreur est survenue lors du téléchargement.");
    }
    setIsLoading(false);
    setPopoverOpen(false);
  };

  const formatJsDate = (date) => {
    const newDate = new Date(date);
    const day = String(newDate.getDate()).padStart(2, "0");
    const month = String(newDate.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so we add 1
    const year = newDate.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const handleSort = (field) => {
    if (sortField === field)
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  function formatNumber(number) {
    const parts = number.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return parts.join(",");
  }
  const navigate = useNavigate();

  const loadBills = () => {
    axios
      .get("facture/freelance")
      .then((data) => {
        console.log("Factures clients:", data.data);
        setBills(data.data);
      })
      .catch((e) => {
        if (e.response?.data?.statusCode === 403) {
          localStorage.clear();
          navigate("/login");
        }
        console.error("Erreur lors du chargement des factures clients:", e);
      });
  };

  const loadFreelanceBills = () => {
    axios
      .get("facture/freelance-to-agency")
      .then((data) => {
        console.log("Factures freelance vers agence:", data.data);
        setFreelanceBills(data.data);
      })
      .catch((e) => {
        if (e.response?.data?.statusCode === 403) {
          localStorage.clear();
          navigate("/login");
        }
        console.error("Erreur lors du chargement des factures freelance:", e);
        // En cas d'erreur, on peut initialiser avec des données de test
        setFreelanceBills([]);
      });
  };

  // Données à afficher selon la section active
  const currentData = activeSection === "clients" ? bills : freelanceBills;

  const filteredBills = currentData
    .filter((f) => {
      if (currentTab === "soldees" && f.FactureRegle !== "OUI") return false;
      if (currentTab === "nonSoldees" && f.FactureRegle === "OUI") return false;
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();

      // Adaptation des champs selon le type de facture
      const clientName = activeSection === "clients" ? f.RaisonSociale : f.agencyName || f.destinataire;
      const factureId = activeSection === "clients" ? f.IdFacture : f.numeroFacture || f.IdFacture;

      return (
        (searchType === "all" &&
          (clientName?.toLowerCase().includes(term) || factureId?.includes(term))) ||
        (searchType === "client" && clientName?.toLowerCase().includes(term)) ||
        (searchType === "numero" && factureId?.includes(term))
      );
    })
    .sort((a, b) => {
      if (!sortField) return 0;
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (sortField === "DateEcheance" || sortField === "dateDePaiement") {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

  const handleAddFacture = () => {
    // Cette fonction devrait appeler l'API pour créer une nouvelle facture client
    // Pour l'instant, on recharge les données
    loadBills();
    toast.success("Facture client ajoutée avec succès");
  };

  const handleAddFreelanceFacture = (factureData) => {
    // Cette fonction devrait appeler l'API pour créer une nouvelle facture freelance
    // Pour l'instant, on ajoute localement et on recharge
    setFreelanceBills(prev => [...prev, {
      ...factureData,
      _id: Date.now().toString(),
      DateEcheance: factureData.dateEcheance,
      TotalTTC: factureData.montant,
    }]);
    toast.success("Facture déposée avec succès à l'agence");
  };

  const handlePartialPayment = (fid, pay) => {
    // Cette fonction devrait appeler l'API pour enregistrer un paiement partiel
    // Pour l'instant, on simule la mise à jour locale
    setBills(
      bills.map((f) => {
        if (f._id === fid) {
          const paiementTotal = (f.paiementTotal || 0) + pay.montant;
          const restant = f.TotalTTC - paiementTotal;
          return {
            ...f,
            paiementTotal,
            montantRestant: restant > 0 ? restant : 0,
            FactureRegle: restant <= 0 ? "OUI" : "NON",
            FacturePartiellementReglee: restant > 0 && paiementTotal > 0,
            dateDePaiement: restant <= 0 ? pay.datePaiement : f.dateDePaiement,
          };
        }
        return f;
      }),
    );
    toast.success("Paiement partiel enregistré");
  };

  const calculerTotaux = () => {
    // Utilise les données de la section active
    const dataToCalculate = currentData;

    // Calcul pour toutes les factures
    const montantTotal = dataToCalculate.reduce(
      (acc, facture) => acc + parseFloat(facture.TotalTTC || facture.montant || 0),
      0,
    );

    // Calcul des montants par type de statut
    const montantFacturesSoldees = dataToCalculate
      .filter((facture) => facture.FactureRegle === "OUI" || facture.statut === "soldée")
      .reduce((acc, facture) => acc + parseFloat(facture.TotalTTC || facture.montant || 0), 0);

    const montantFacturesNonSoldees = dataToCalculate
      .filter((facture) => (facture.FactureRegle !== "OUI" && !facture.FacturePartiellementReglee) || facture.statut === "non soldée")
      .reduce((acc, facture) => acc + parseFloat(facture.TotalTTC || facture.montant || 0), 0);

    const montantFacturesPartielles = dataToCalculate
      .filter((facture) => facture.FacturePartiellementReglee || facture.statut === "partiellement payée")
      .reduce((acc, facture) => acc + parseFloat(facture.TotalTTC || facture.montant || 0), 0);

    // Calcul du montant restant à payer (montant dû)
    const montantDu = dataToCalculate
      .filter((facture) => (facture.FactureRegle !== "OUI") || facture.statut !== "soldée")
      .reduce((acc, facture) => {
        const montantFacture = parseFloat(facture.TotalTTC || facture.montant || 0);
        if ((facture.FacturePartiellementReglee || facture.statut === "partiellement payée") && facture.montantRestant !== undefined) {
          return acc + parseFloat(facture.montantRestant || 0);
        }
        return acc + montantFacture;
      }, 0);

    // Comptage du nombre de factures par statut
    const facturesSoldees = dataToCalculate.filter(
      (facture) => facture.FactureRegle === "OUI" || facture.statut === "soldée",
    ).length;
    const facturesNonSoldees = dataToCalculate.filter(
      (facture) => (facture.FactureRegle !== "OUI" && !facture.FacturePartiellementReglee) || facture.statut === "non soldée",
    ).length;
    const facturesPartielles = dataToCalculate.filter(
      (facture) => facture.FacturePartiellementReglee || facture.statut === "partiellement payée",
    ).length;

    return {
      montantTotal,
      montantDu,
      montantFacturesSoldees,
      montantFacturesNonSoldees,
      montantFacturesPartielles,
      facturesSoldees,
      facturesNonSoldees,
      facturesPartielles,
      totalFactures: dataToCalculate.length,
    };
  };

  const totaux = calculerTotaux();

  return (
    <div className="w-full scrollbar-none text-[10px]">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
        </div>
      )}
      <div className="flex flex-wrap justify-between gap-4 mb-6 items-center">
        <PageHeading icon="invoices" title="Gestion des factures" description="Préparez et suivez la facturation de vos clients." />
        {activeSection !=="clients" &&<Button
          className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() =>  setOpenFreelanceModal(true)}
        >
          + { "Déposer une facture à l'agence"}
        </Button>}
      </div>

      {/* Navigation entre les sections */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveSection("clients")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSection === "clients"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <span>Factures Clients</span>
              </div>
            </button>
            <button
              onClick={() => setActiveSection("freelance")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeSection === "freelance"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Mes Factures vers l'Agence</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4 mt-2">
        {/* Carte 1: Montant total des factures */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-2">
            <div className="rounded-full p-2 bg-blue-50">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-500 ml-2">
              Total facturé
            </h3>
          </div>
          <p className="text-2xl  text-gray-800">
            {formatMontant(totaux.montantTotal)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totaux.totalFactures} factures au total
          </p>
        </div>

        {/* Carte 2: Factures soldées */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-2">
            <div className="rounded-full p-2 bg-green-50">
              <svg
                className="h-5 w-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-500 ml-2">
              Factures soldées
            </h3>
          </div>
          <p className="text-2xl  text-gray-800">
            {formatMontant(totaux.montantFacturesSoldees)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totaux.facturesSoldees} factures
          </p>
        </div>

        {/* Carte 3: Factures non-soldées */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-2">
            <div className="rounded-full p-2 bg-orange-50">
              <svg
                className="h-5 w-5 text-orange-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-500 ml-2">
              Non-soldées
            </h3>
          </div>
          <p className="text-2xl  text-gray-800">
            {formatMontant(totaux.montantFacturesNonSoldees)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totaux.facturesNonSoldees} factures
          </p>
        </div>

        {/* Carte 4: Factures partielles */}
        {/*  <div className="bg-white p-4 rounded-lg shadow border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-2">
            <div className="rounded-full p-2 bg-blue-50">
              <svg
                className="h-5 w-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-500 ml-2">
              Partiellement payées
            </h3>
          </div>
          <p className="text-2xl  text-gray-800">
            {formatMontant(totaux.montantFacturesPartielles)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {totaux.facturesPartielles} factures
          </p>
        </div> */}

        {/* Carte 5: Montant dû */}
        {/* <div className="bg-white p-4 rounded-lg shadow border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center mb-2">
            <div className="rounded-full p-2 bg-red-50">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-500 ml-2">
              Reste à payer
            </h3>
          </div>
          <p className="text-2xl  text-red-600">
            {formatMontant(totaux.montantDu)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {Math.round((totaux.montantDu / totaux.montantTotal) * 100)}% du
            total facturé
          </p>
        </div> */}
      </div>

      <div className="flex flex-col md:flex-row justify-between mb-6 bg-gray-50 p-4 rounded-lg">
        <Tabs
          value={currentTab}
          onValueChange={setCurrentTab}
          className="w-full md:w-auto"
        >
          <TabsList className="grid grid-cols-3 w-full md:w-[400px]">
            <TabsTrigger value="all" className="text-sm font-medium">
              Toutes
            </TabsTrigger>
            <TabsTrigger value="soldees" className="text-sm font-medium">
              Soldées
            </TabsTrigger>
            <TabsTrigger value="nonSoldees" className="text-sm font-medium">
              À payer
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-2 items-center">
          <div className="flex items-center rounded-md overflow-hidden  shadow-sm w-full md:w-auto">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="py-1 px-2 w-28 text-sm border-r rounded-md focus:outline-none bg-gray-50 h-9"
            >
              <option value="all">Tous</option>
              <option value="client">Client</option>
              <option value="numero">N° Facture</option>
            </select>
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="py-1 px-3 pl-8 w-full md:w-48 text-sm focus:outline-none h-9 border-0"
              />
              <svg
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              {searchTerm && (
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setSearchTerm("")}
                  title="Effacer"
                >
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <table className="table-fixed w-full text-sm text-left">
        <thead className="bg-gray-100 text-xs">
          <tr>
            <th
              onClick={() => handleSort(activeSection === "clients" ? "RaisonSociale" : "agencyName")}
              className="px-4 py-2 cursor-pointer"
            >
              {activeSection === "clients" ? "CLIENT" : "AGENCE"}
            </th>
            <th
              onClick={() => handleSort(activeSection === "clients" ? "IdFacture" : "numeroFacture")}
              className="px-4 py-2 cursor-pointer"
            >
              N°FACTURE
            </th>
            <th
              onClick={() => handleSort("DateEcheance")}
              className="px-4 py-2 cursor-pointer"
            >
              DATE ECHEANCE
            </th>
            <th
              onClick={() => handleSort(activeSection === "clients" ? "TotalTTC" : "montant")}
              className="px-4 py-2 cursor-pointer"
            >
              MONTANT TTC
            </th>
            <th
              onClick={() => handleSort("FactureRegle")}
              className="px-4 py-2 cursor-pointer"
            >
              STATUT
            </th>
            {activeSection === "clients" && (
              <th
                onClick={() => handleSort("EstFacto")}
                className="px-4 py-2 cursor-pointer"
              >
                FACTO
              </th>
            )}
            {activeSection === "clients" && <th className="px-4 py-2">RELANCE</th>}
            <th
              onClick={() => handleSort("dateDePaiement")}
              className="px-4 py-2 cursor-pointer"
            >
              DATE DU PAIEMENT
            </th>
            <th className="px-4 py-2">ACTION</th>
          </tr>
        </thead>

        <tbody>
          {filteredBills.map((bill, idx) => (
            <tr key={idx} className="border-b text-xs hover:bg-gray-50">
              <td className="px-4 py-2 w-1/5 ">
                <svg
                  className="w-4 h-4 text-gray-500 inline-block align-middle"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2
           2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9
           7h1m-1 4h1m4-4h1m-1 4h1m-5
           10v-5a1 1 0 011-1h2a1 1 0
           011 1v5m-4 0h4"
                  />
                </svg>
                <span className="overflow-hidden">
                  {activeSection === "clients"
                    ? bill.RaisonSociale
                    : (bill.agencyName || bill.destinataire || "Agence")
                  }
                </span>
              </td>
              <td className="px-4 py-2">
                <div className="inline-flex items-center">
                  <svg
                    className="mx-1 w-4 h-4 text-blue-500 inline-block align-middle"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0
           01-2-2V5a2 2 0 012-2h5.586a1 1
           0 01.707.293l5.414 5.414a1 1
           0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  {activeSection === "clients"
                    ? bill.IdFacture
                    : (bill.numeroFacture || bill.IdFacture)
                  }
                </div>
              </td>
              <td className="px-4 py-2">
                <div className="inline-flex items-center">
                  <svg
                    className="mx-1 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  {formatDateYearString(bill.DateEcheance)}
                </div>
              </td>
              <td className="px-4 py-2">
                <div className="inline-flex items-center">
                  <svg
                    className="mx-1 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {formatNumber(activeSection === "clients" ? bill.TotalTTC : (bill.montant || bill.TotalTTC))}€{" "}
                </div>
              </td>
              <td className="px-4 py-2">
                <div className="inline-flex items-center">
                  {bill.FactureRegle === "OUI" ? (
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-200 border-green-200">
                      Soldée
                    </Badge>
                  ) : bill.FacturePartiellementReglee ? (
                    <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 border-yellow-200">
                      Partiellement soldée
                    </Badge>
                  ) : (
                    <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200">
                      Non soldée
                    </Badge>
                  )}
                </div>
              </td>

              {activeSection === "clients" && (
                <td className="px-4 py-2">
                  <div className="inline-flex items-center">
                    {bill.EstFacto === "OUI" ? (
                      <p className="px-2 py-1 rounded-md">OUI</p>
                    ) : (
                      <p className="px-2 py-1 rounded-md">NON</p>
                    )}
                  </div>
                </td>
              )}

              {activeSection === "clients" && (
                <td className="px-4 py-3">
                  <div className="flex justify-start">
                    {!bill.relance || bill.relance.length === 0 ? (
                      <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-200">
                        Aucune
                      </Badge>
                    ) : bill.relance.length === 1 ? (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-200">
                        Niveau 1
                      </Badge>
                    ) : bill.relance.length === 2 ? (
                      <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-200 border-orange-200">
                        Niveau 2
                      </Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 hover:bg-red-200 border-red-200">
                        Niveau 3+
                      </Badge>
                    )}
                  </div>
                </td>
              )}

              {/* <td className="px-4 py-2 text-center">
                <div className="mb-2"></div>
                <div className="space-y-2">
                  {bill.relance.map((rel) => (
                    <div
                      key={`${rel.date}-${rel.contactEmail}`}
                      className="space-y-1"
                    >
                      {rel.message===""&&<p className="text-xs italic">Relance {rel.message}</p>}
                      <p className="text-xs italic font-semibold">
                        le {formatJsDate(rel.date)} à {formatJsHour(rel.date)}
                      </p>
                      <p className="text-xs italic">à {rel.contactEmail}</p>
                    </div>
                  ))}
                </div>
              </td> */}

              <td className="px-4 py-2">
                {bill.dateDePaiement && formatJsDate(bill.dateDePaiement)}
              </td>
              <td className="px-4 py-2">
                <Popover onOpenChange={setPopoverOpen} className="bg-white">
                  <PopoverTrigger className="bg-white">
                    <Button className="bg-white text-gray-500" size="sm">
                      •••
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-60 p-2 bg-white">
                    <div className="grid gap-1">
                      <Button
                        onClick={() => handleDownloadPdf(bill)}
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                        Voir
                      </Button>

                      {/*  <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          setOpenPartialPaymentModal(true)
                        }}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                        Paiement partiel
                      </Button>
 */}
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="justify-start text-sm"
                        onClick={() => {
                          setIsRelanceOpen(true);
                          setSelectedBill(bill);
                        }}
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                          />
                        </svg>
                        Relancer
                      </Button>
                      <Dialog open={openRelance} onOpenChange={setOpenRelance}>
                        <DialogContent className="max-w-md p-0 overflow-hidden bg-white rounded-lg shadow-lg">
                          {/* En-tête */}
                          <div className="bg-gradient-to-r from-blue-700 to-blue-800 py-6 px-8">
                            <DialogTitle className="text-2xl  text-white">
                              Niveau de relance
                            </DialogTitle>
                            <p className="text-blue-100 mt-1">
                              Choisissez un niveau de relance pour ce client
                            </p>
                          </div>

                          {/* Corps */}
                          <div className="p-8 space-y-6">
                            <RadioGroup
                              value={niveauRelance.toString()}
                              onValueChange={(v) =>
                                setNiveauRelance(parseInt(v))
                              }
                              className="space-y-4"
                            >
                              {[0, 1, 2, 3].map((n) => (
                                <div
                                  key={n}
                                  className={`flex items-center p-3 rounded-md border ${
                                    n === 0
                                      ? "bg-white border-gray-200"
                                      : n === 1
                                        ? "bg-blue-50 border-blue-200"
                                        : n === 2
                                          ? "bg-orange-50 border-orange-200"
                                          : "bg-red-50 border-red-200"
                                  }`}
                                >
                                  <RadioGroupItem
                                    value={n.toString()}
                                    id={`relance-${n}`}
                                  />
                                  <Label
                                    htmlFor={`relance-${n}`}
                                    className="ml-2 cursor-pointer"
                                  >
                                    {n === 0 ? "Aucune" : `Niveau ${n}`}
                                  </Label>
                                </div>
                              ))}
                            </RadioGroup>
                          </div>

                          {/* Pied de modal */}
                          <DialogFooter className="flex justify-end px-8 py-6 border-t border-gray-200 bg-gray-50">
                            <Button
                              variant="outline"
                              onClick={() => setOpenRelance(false)}
                              className="border-gray-300 hover:bg-gray-100"
                            >
                              Annuler
                            </Button>
                            <Button
                              className="ml-3 bg-blue-700 hover:bg-blue-800 text-white"
                              // onClick={sendRelance}
                            >
                              Envoyer la relance
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </PopoverContent>
                </Popover>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <FactureModal
        open={openModal}
        onOpenChange={setOpenModal}
        onSubmit={handleAddFacture}
      />

      <FreelanceFactureModal
        open={openFreelanceModal}
        onOpenChange={setOpenFreelanceModal}
        onSubmit={handleAddFreelanceFacture}
      />

      <ClientRelanceModal
        open={isRelanceOpen}
        onOpenChange={setIsRelanceOpen}
        niveauRelance={niveauRelance}
        setNiveauRelance={setNiveauRelance}
        //onSubmit={handleRelanceSubmit}
      />
      {selectedFacture && (
        <PartialPaymentModal
          open={openPartialPaymentModal}
          onOpenChange={setOpenPartialPaymentModal}
          facture={selectedFacture}
          onSubmit={handlePartialPayment}
        />
      )}
    </div>
  );
}

